import { cn } from '@/lib/cn';
import { type ExtensionTask } from '@/types';
import {
  Evidence,
  LibraryBig,
  Microphone,
  Microscope,
  TextSearch,
} from '@heidi/design-system/icons';
import { TypographyH5, TypographyP } from '@heidi/design-system/typography';
import { ActionType, type ExtensionTaskId } from '@heidi/schema/ml-scribe';
import {
  AudioLines,
  Blocks,
  Check,
  Download,
  MessageSquare,
  PencilLine,
  UserRoundPlus,
} from 'lucide-react';

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>;

const TASK_ICONS: Record<string, IconComponent> = {
  invite_team_member: UserRoundPlus,
  select_note_template: Blocks,
  create_custom_template: Blocks,
  create_notes: PencilLine,
  submit_evidence_queries: Evidence,
  use_source_control: TextSearch,
};

const TASK_ICONS_BY_ICON_KEY: Record<string, IconComponent> = {
  'user-round-plus': UserRoundPlus,
  blocks: Blocks,
  'pencil-line': PencilLine,
  'flask-conical': Evidence,
  microscope: Microscope,
  'text-search': TextSearch,
  'library-big': LibraryBig,
  microphone: Microphone,
  'audio-lines': AudioLines,
  'message-square': MessageSquare,
  // Backend-confirmed TaskIcon key for the "download desktop app" task.
  download: Download,
};

type TaskActionHandler = () => void;

export interface TrialExtensionTaskRowProps {
  task: ExtensionTask;
  onAction?: TaskActionHandler;
  onTaskClick?: (taskId: ExtensionTaskId) => void;
  /** 'tier' = Pro/Clinician modal (gap-3, P); 'practice' = Practice modal (gap-1, H5) */
  variant: 'tier' | 'practice';
}

export function TrialExtensionTaskRow({
  task,
  onAction,
  onTaskClick,
  variant,
}: TrialExtensionTaskRowProps) {
  const isComplete = task.completed;
  const actionUrl = task.action_url ?? undefined;
  const hasCustomHandler = !!onAction;
  const isExternalLink =
    !hasCustomHandler && task.action_type === ActionType.link && actionUrl;

  const handleClick = () => {
    onTaskClick?.(task.id);
    if (hasCustomHandler) onAction();
    else if (isExternalLink && actionUrl) {
      window.open(actionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const Icon = TASK_ICONS_BY_ICON_KEY[task.icon] ?? TASK_ICONS[task.id] ?? PencilLine;
  const showCounter = task.target >= 1;
  const isTier = variant === 'tier';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full cursor-pointer items-center rounded-lg text-start transition-colors hover:bg-background-secondary',
        isTier ? 'gap-3 px-2 py-4' : 'gap-1 py-4'
      )}
    >
      <Icon
        size={18}
        className={cn(
          'shrink-0',
          isComplete ? 'text-text-tertiary' : 'text-text-primary'
        )}
      />
      {isTier ? (
        <TypographyP className={cn('flex-1', isComplete && 'text-text-tertiary')}>
          {task.title}
        </TypographyP>
      ) : (
        <TypographyH5 className={cn('flex-1', isComplete && 'text-text-tertiary')}>
          {task.title}
        </TypographyH5>
      )}
      {showCounter && (
        <span
          className={cn(
            'text-sm',
            isComplete ? 'text-text-tertiary' : 'text-text-secondary'
          )}
        >
          {task.current}/{task.target}
        </span>
      )}
      <div
        className={cn(
          'ml-1 flex size-5 shrink-0 items-center justify-center rounded-full border',
          isComplete
            ? 'border-semantic-border-success-strong bg-semantic-fill-success'
            : 'border-background-secondary bg-background-secondary'
        )}
      >
        <Check
          size={12}
          className={cn(isComplete ? 'text-white' : 'text-text-tertiary')}
          strokeWidth={2.5}
        />
      </div>
    </button>
  );
}
